import { gql } from 'graphql-request';
import type { CardQueryFilters } from '@ygoargentina/helpers/card-query';
import { getGraphQLQueryClient } from './query-client';
import type { Nullable, WithData } from './api.types';

export type CardSearchSource =
  | 'empty'
  | 'name'
  | 'rules'
  | 'text'
  | 'partial'
  | 'fallback'
  | 'filters';

export type CardSearchHit = {
  konamiId: number;
  name: string;
  nameSp: Nullable<string>;
  image: Nullable<string>;
  type: string;
  attribute: Nullable<string>;
  race: Nullable<string>;
  level: Nullable<number>;
  linkRating: Nullable<number>;
  scale: Nullable<number>;
  atk: Nullable<number>;
  def: Nullable<number>;
  description: string;
  descriptionSp: Nullable<string>;
  firstReleaseDate: Nullable<string>;
};

export type CardSearchResult = {
  filters: CardQueryFilters;
  source: CardSearchSource;
  data: CardSearchHit[];
  meta: {
    firstPage: Nullable<number>;
    lastPage: Nullable<number>;
    page: Nullable<number>;
    perPage: Nullable<number>;
    total: Nullable<number>;
  };
};

export type CardDetailSetPrice = {
  source: string;
  price: number;
  currency: string;
  updatedAt: Nullable<string>;
};

export type CardDetailSet = {
  cardSetId: number;
  code: string;
  setName: string;
  rarity: string;
  label: string;
  releaseDate: Nullable<string>;
  image: Nullable<string>;
  prices: CardDetailSetPrice[];
};

export type CardDetailBanlist = {
  banlistId: number;
  name: string;
  formatName: string;
  organizerName: Nullable<string>;
  status: string;
  conditional: boolean;
};

export type CardDetailRuling = {
  id: number;
  title: string;
  description: string;
};

export type CardDetailDecklist = {
  id: number;
  name: string;
  tournamentName: string;
  tournamentDate: string;
  placement: string;
  metaDeckName: Nullable<string>;
  sourceUrl: Nullable<string>;
};

export type CardDetail = {
  konamiId: number;
  name: string;
  nameSp: Nullable<string>;
  slug: string;
  image: Nullable<string>;
  images: Nullable<unknown>;
  type: string;
  frameType: string;
  attribute: Nullable<string>;
  archetype: Nullable<string>;
  data: Nullable<{
    race: Nullable<string>;
    level: Nullable<number>;
    linkval: Nullable<number>;
    scale: Nullable<number>;
    atk: Nullable<string>;
    def: Nullable<string>;
  }>;
  description: string;
  descriptionSp: Nullable<string>;
  genesysPoints: number;
  firstReleaseDate: Nullable<string>;
  sets: CardDetailSet[];
  banlists: CardDetailBanlist[];
  rulings: CardDetailRuling[];
  usage: {
    decklistCount: number;
    recent: CardDetailDecklist[];
  };
};

export type CardDatabaseSearchParams = {
  query?: string;
  filters?: CardQueryFilters;
  page?: number;
};

/** Búsqueda pública de la base de cartas: texto libre y/o filtros. */
export const search = async ({
  query,
  filters,
  page,
}: CardDatabaseSearchParams = {}) => {
  // Sólo viajan las variables que vienen: el server distingue "sin filtros" de {}.
  const variables: Record<string, unknown> = {};
  if (query !== undefined) variables.query = query;
  if (filters) variables.filters = filters;
  if (page !== undefined) variables.page = page;

  return getGraphQLQueryClient(null).request<{ data: CardSearchResult }>(
    gql`
      query ($query: String, $filters: CardQueryFiltersInput, $page: Int) {
        data: publicCardSearch(query: $query, filters: $filters, page: $page) {
          source
          filters {
            name
            attributes
            races
            cardKinds
            monsterKinds
            spellTrapKinds
            concepts
            level { min max }
            linkRating { min max }
            scale { min max }
            atk { min max }
            def { min max }
            archetype
            releasedAfter
            releasedBefore
            text
          }
          data {
            konamiId
            name
            nameSp
            image
            type
            attribute
            race
            level
            linkRating
            scale
            atk
            def
            description
            descriptionSp
            firstReleaseDate
          }
          meta {
            firstPage
            lastPage
            page
            perPage
            total
          }
        }
      }
    `,
    variables
  );
};

/** Ficha pública de una carta (sets, banlists, rulings y uso en mazos). */
export const detail = async (konamiId: number) =>
  getGraphQLQueryClient(null).request<WithData<Nullable<CardDetail>>>(
    gql`
      query ($konamiId: Int!) {
        data: publicCardDatabaseDetail(konamiId: $konamiId) {
          konamiId
          name
          nameSp
          slug
          image
          images
          type
          frameType
          attribute
          archetype
          data {
            race
            level
            linkval
            scale
            atk
            def
          }
          description
          descriptionSp
          genesysPoints
          firstReleaseDate
          sets {
            cardSetId
            code
            setName
            rarity
            label
            releaseDate
            image
            prices {
              source
              price
              currency
              updatedAt
            }
          }
          banlists {
            banlistId
            name
            formatName
            organizerName
            status
            conditional
          }
          rulings {
            id
            title
            description
          }
          usage {
            decklistCount
            recent {
              id
              name
              tournamentName
              tournamentDate
              placement
              metaDeckName
              sourceUrl
            }
          }
        }
      }
    `,
    { konamiId }
  );
